import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { File, FileImage, FileSpreadsheet, FileText, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { listJobAttachments, type JobAttachment, type JobAttachmentLink } from '@/lib/api';
import {
  readAttachmentList,
  readSavedAttachment,
  saveAttachmentList,
  saveJobAttachments,
  savedAttachmentIds,
  type SavedState,
} from '@/lib/offline/attachment-cache';

/**
 * The files the manager attached to a job (CAPTURE_JOBS.md section 8.5),
 * listed under the instructions.
 *
 * With signal, the list comes from the API and every file is saved on the
 * phone in the background (src/lib/offline/attachment-cache.ts), so it opens
 * later in an aisle with no signal. A saved file always opens from the
 * phone. A file not saved yet opens from its download URL, which expires
 * after 15 minutes, so a link older than 10 minutes is renewed before it
 * opens. With no signal, the list saved at the last visit is shown, and only
 * the files saved on the phone can be opened.
 */

const MAX_LINK_AGE_MS = 10 * 60 * 1000;

type Item = JobAttachment & { downloadUrl?: string };

type State =
  | { kind: 'loading' }
  | { kind: 'loaded'; items: Item[]; loadedAt: number; savedListAt?: string }
  | { kind: 'failed' };

function attachmentIcon(contentType: string, fileName: string): { Icon: LucideIcon; label: string } {
  const type = contentType.toLowerCase();
  const name = fileName.toLowerCase();
  if (type === 'application/pdf' || name.endsWith('.pdf')) return { Icon: FileText, label: 'PDF' };
  if (type.startsWith('image/')) return { Icon: FileImage, label: 'Image' };
  if (type === 'text/csv' || type.includes('spreadsheet') || type === 'application/vnd.ms-excel') {
    return { Icon: FileSpreadsheet, label: 'Spreadsheet' };
  }
  if (type.includes('wordprocessingml') || type === 'application/msword') return { Icon: FileText, label: 'Document' };
  return { Icon: File, label: 'File' };
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatSavedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** The line under a file name that says whether it opens with no signal. */
function savedLine(saved: SavedState | undefined, online: boolean): { text: string; tone: string } | null {
  if (saved === 'saved') return { text: 'Saved on this phone', tone: 'text-success' };
  if (saved === 'saving') return { text: 'Saving to this phone...', tone: 'text-muted' };
  if (saved === 'no-space') return { text: 'Not saved: not enough space on this phone', tone: 'text-warning' };
  if (!online) return { text: 'Needs signal to open', tone: 'text-warning' };
  if (saved === 'failed') return { text: 'Not saved on this phone yet', tone: 'text-muted' };
  return null;
}

export function JobAttachments({ jobId }: { jobId: string }) {
  const online = useOnlineStatus();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [saved, setSaved] = useState<Record<string, SavedState>>({});
  /** Blob URLs of saved files, kept when the phone blocked the first open. */
  const [ready, setReady] = useState<Record<string, string>>({});
  const stateRef = useRef(state);
  stateRef.current = state;
  const readyRef = useRef(ready);
  readyRef.current = ready;

  const markSaved = useCallback(async () => {
    const ids = await savedAttachmentIds(jobId).catch(() => new Set<string>());
    setSaved((current) => {
      const next: Record<string, SavedState> = {};
      for (const [id, value] of Object.entries(current)) if (value !== 'saved') next[id] = value;
      for (const id of ids) next[id] = 'saved';
      return next;
    });
  }, [jobId]);

  const showSavedList = useCallback(async (): Promise<boolean> => {
    const list = await readAttachmentList(jobId).catch(() => null);
    if (!list) return false;
    setState({ kind: 'loaded', items: list.items, loadedAt: Date.now(), savedListAt: list.savedAt });
    await markSaved();
    return true;
  }, [jobId, markSaved]);

  const load = useCallback(
    async (quiet: boolean): Promise<JobAttachmentLink[] | null> => {
      if (!quiet) setState({ kind: 'loading' });
      if (!navigator.onLine) {
        if (!(await showSavedList()) && !quiet) setState({ kind: 'failed' });
        return null;
      }
      try {
        const response = await listJobAttachments(jobId);
        setState({ kind: 'loaded', items: response.items, loadedAt: Date.now() });
        await markSaved();
        void saveAttachmentList(jobId, response.items).catch(() => undefined);
        void saveJobAttachments(jobId, response.items, ({ attachmentId, state: next }) =>
          setSaved((current) => ({ ...current, [attachmentId]: next }))
        ).catch(() => undefined);
        return response.items;
      } catch {
        // A quiet refresh keeps the list already on screen. Otherwise the
        // list saved at the last visit stands in, and only then an error.
        if (quiet && stateRef.current.kind === 'loaded') return null;
        if (!(await showSavedList())) setState({ kind: 'failed' });
        return null;
      }
    },
    [jobId, markSaved, showSavedList]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(
    () => () => {
      for (const url of Object.values(readyRef.current)) URL.revokeObjectURL(url);
    },
    []
  );

  // Back online, or back to the view with aged links: fetch the list again,
  // which also saves any file added since.
  useEffect(() => {
    function refreshIfOld() {
      if (document.visibilityState === 'hidden' || !navigator.onLine) return;
      const current = stateRef.current;
      if (current.kind !== 'loaded') return;
      if (current.savedListAt || Date.now() - current.loadedAt > MAX_LINK_AGE_MS) void load(true);
    }
    document.addEventListener('visibilitychange', refreshIfOld);
    window.addEventListener('focus', refreshIfOld);
    window.addEventListener('online', refreshIfOld);
    return () => {
      document.removeEventListener('visibilitychange', refreshIfOld);
      window.removeEventListener('focus', refreshIfOld);
      window.removeEventListener('online', refreshIfOld);
    };
  }, [load]);

  async function openFile(event: MouseEvent<HTMLAnchorElement>, item: Item) {
    // A saved file whose first open was blocked already has its URL on the
    // link, so the browser follows it directly.
    if (readyRef.current[item.attachmentId]) return;

    if (saved[item.attachmentId] === 'saved') {
      event.preventDefault();
      const blob = await readSavedAttachment(item.attachmentId).catch(() => null);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const opened = window.open(url, '_blank', 'noopener');
        // Opening a tab after an await can count as a pop-up. Keep the URL on
        // the link so the next tap opens it with no wait.
        if (opened) setTimeout(() => URL.revokeObjectURL(url), 60_000);
        else setReady((current) => ({ ...current, [item.attachmentId]: url }));
        return;
      }
      // The saved copy is gone (the browser cleared it); fall through to the network.
      setSaved((current) => ({ ...current, [item.attachmentId]: 'failed' }));
    }

    if (!online || !item.downloadUrl) {
      event.preventDefault();
      return;
    }
    const current = stateRef.current;
    if (current.kind !== 'loaded' || Date.now() - current.loadedAt <= MAX_LINK_AGE_MS) {
      if (event.defaultPrevented) window.open(item.downloadUrl, '_blank', 'noopener');
      return;
    }
    // The link is old enough to have nearly expired: renew it, then open.
    event.preventDefault();
    const items = await load(true);
    const fresh = items?.find((candidate) => candidate.attachmentId === item.attachmentId);
    if (fresh) window.open(fresh.downloadUrl, '_blank', 'noopener');
  }

  return (
    <section className="flex flex-col gap-2" aria-labelledby="job-attachments">
      <h2 id="job-attachments" className="text-sm font-semibold text-muted">
        Files
      </h2>

      {state.kind === 'loading' && <p className="text-sm text-muted">Loading the files...</p>}

      {state.kind === 'failed' && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-warning">
            {online
              ? 'The files for this job could not be loaded.'
              : 'Offline, and this job has not been opened here with signal yet, so no files are saved on this phone.'}
          </p>
          {online && (
            <Button variant="outline" onClick={() => void load(false)}>
              Retry
            </Button>
          )}
        </div>
      )}

      {state.kind === 'loaded' && state.items.length === 0 && (
        <p className="text-xs text-muted">No files attached to this job</p>
      )}

      {state.kind === 'loaded' && state.items.length > 0 && (
        <>
          {!online && (
            <p className="text-xs text-warning">Offline. Files saved on this phone still open.</p>
          )}
          {online && state.savedListAt && (
            <p className="text-xs text-muted">
              Showing the list saved on {formatSavedAt(state.savedListAt)}.
            </p>
          )}
          <ul className="flex flex-col divide-y divide-border rounded-card border border-border">
            {state.items.map((item) => {
              const { Icon, label } = attachmentIcon(item.contentType, item.fileName);
              const isSaved = saved[item.attachmentId] === 'saved';
              const openable = isSaved || (online && Boolean(item.downloadUrl));
              const line = savedLine(saved[item.attachmentId], online);
              return (
                <li key={item.attachmentId}>
                  <a
                    href={ready[item.attachmentId] ?? item.downloadUrl ?? '#'}
                    target="_blank"
                    rel="noopener"
                    aria-disabled={!openable || undefined}
                    aria-label={`Open ${item.fileName}, ${label}, ${formatBytes(item.byteLength)}${
                      line ? `, ${line.text}` : ''
                    }`}
                    onClick={(event) => void openFile(event, item)}
                    className={`flex min-h-11 items-center gap-3 px-4 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent ${
                      openable ? 'hover:bg-white/5' : 'pointer-events-none opacity-50'
                    }`}
                  >
                    <Icon className="size-5 shrink-0 text-muted" aria-hidden="true" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm text-surface-foreground">{item.fileName}</span>
                      {line && <span className={`text-xs ${line.tone}`}>{line.text}</span>}
                    </span>
                    <span className="shrink-0 text-xs text-muted">{formatBytes(item.byteLength)}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
